'use client';

import { AssistantRuntimeProvider, RuntimeAdapterProvider, Suggestions, useAui, useLocalRuntime, type ChatModelAdapter } from '@assistant-ui/react';
// @ts-ignore — subpath export not resolved with moduleResolution "node"
import type { AttachmentAdapter, CompleteAttachment, PendingAttachment, ThreadHistoryAdapter } from '@assistant-ui/core';
import { CompositeAttachmentAdapter, ExportedMessageRepository, SimpleImageAttachmentAdapter, SimpleTextAttachmentAdapter } from '@assistant-ui/core';
import { unstable_useRemoteThreadListRuntime as useRemoteThreadListRuntime } from '@assistant-ui/react';
import type { RemoteThreadListAdapter } from '@assistant-ui/core';
import { createAssistantStream } from 'assistant-stream';
import { useRouter } from 'next/router';
import { useMemo, useRef, type FC, type MutableRefObject, type ReactNode } from 'react';
import { toast } from 'react-toastify';
import { useAssistantEnabled } from '~/components/assistant-ui/AssistantEnabledContext';
import { S3DocProvider, useS3Docs } from '~/components/assistant-ui/s3-documents/S3DocContext';
import { buildS3DocMetaMarker, type S3DocMeta, type S3DocRef, type S3DocSelection } from '~/components/assistant-ui/s3-documents/types';
import { TooltipProvider } from '~/components/shadcn/Tooltip';
import { mastraThreadListAdapter } from '~/utils/assistant-ui/mastraThreadListAdapter';
import { readAssistantReply } from '~/utils/assistant-ui/assistantReply';
import { isDictationSupported, OpenAiDictationAdapter } from '~/utils/assistant-ui/openAiDictationAdapter';

// The feature flag is checked where the mic button renders: this provider sits above FeatureFlagProvider and UserProvider.
const dictationAdapter = isDictationSupported() ? new OpenAiDictationAdapter() : undefined;

// File size limits (before base64 encoding, which inflates ~33%)
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

/** Wraps a CompositeAttachmentAdapter with per-file and total size limits. */
class SizeLimitedAdapter implements AttachmentAdapter {
	private fileSizes = new Map<string, number>();

	constructor(
		private inner: AttachmentAdapter,
		private maxBytesTotal: number
	) {}

	get accept() {
		return this.inner.accept;
	}

	add(state: { file: File }): ReturnType<AttachmentAdapter['add']> {
		const limitMB = Math.round(this.maxBytesTotal / 1024 / 1024);
		const currentTotal = Array.from(this.fileSizes.values()).reduce((sum, s) => sum + s, 0);
		if (currentTotal + state.file.size > this.maxBytesTotal) {
			toast.error(`Total attachments exceed ${limitMB} MB limit`);
			throw new Error(`Total attachments exceed ${limitMB} MB limit`);
		}
		const result = this.inner.add(state);
		// Track file size once the pending attachment is created
		const trackSize = (pending: PendingAttachment) => {
			this.fileSizes.set(pending.id, state.file.size);
		};
		if (Symbol.asyncIterator in result) {
			const fileSizes = this.fileSizes;
			const fileSize = state.file.size;
			const iter = result as AsyncGenerator<PendingAttachment>;
			return (async function* () {
				let last: PendingAttachment | undefined;
				for await (const pending of iter) {
					last = pending;
					yield pending;
				}
				if (last) fileSizes.set(last.id, fileSize);
			})();
		}
		return (result as Promise<PendingAttachment>).then((pending) => {
			trackSize(pending);
			return pending;
		});
	}

	send(attachment: PendingAttachment) {
		return this.inner.send(attachment);
	}

	remove(attachment: Parameters<AttachmentAdapter['remove']>[0]) {
		this.fileSizes.delete(attachment.id);
		return this.inner.remove(attachment);
	}
}

class PDFAttachmentAdapter implements AttachmentAdapter {
	accept = 'application/pdf';

	async add({ file }: { file: File }): Promise<PendingAttachment> {
		return {
			id: crypto.randomUUID(),
			type: 'document',
			name: file.name,
			file,
			status: { type: 'running', reason: 'uploading', progress: 0 },
		};
	}

	async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
		const arrayBuffer = await attachment.file.arrayBuffer();
		const bytes = new Uint8Array(arrayBuffer);
		let binary = '';
		bytes.forEach((byte) => {
			binary += String.fromCharCode(byte);
		});
		const base64 = btoa(binary);

		return {
			id: attachment.id,
			type: 'document',
			name: attachment.name,
			content: [
				{
					type: 'file' as const,
					data: base64,
					mimeType: 'application/pdf',
				},
			],
			status: { type: 'complete' },
		};
	}

	async remove(): Promise<void> {}
}

const suggestions = [
	{
		label: 'Track deliveries and arrival dates',
		prompt: 'Are there any purchase orders that are past due or arriving soon?',
		title: 'PO delivery status',
	},
	{
		label: 'Check outstanding payments',
		prompt: 'Which jobs have overdue balances or need invoicing?',
		title: 'Overdue payments',
	},
	{
		label: 'See who is on the clock and where',
		prompt: "What's the current status of my technicians?",
		title: 'Technician status',
	},
	{
		label: 'View upcoming installs and measures',
		prompt: "What's on the schedule for this week?",
		title: "This week's schedule",
	},
	{
		label: 'Review business performance',
		prompt: 'Show me key business metrics for this month — pipeline, revenue, and activity.',
		title: 'Monthly KPIs',
	},
	{
		label: 'Find customers needing payment upfront',
		prompt: 'Show me customers that are on accounting hold or require payment upfront.',
		title: 'Accounting holds',
	},
];

const useChatRuntimeHook = (s3DocsRef: MutableRefObject<S3DocSelection[]>, clearS3Docs: () => void) => {
	const aui = useAui();
	// Accumulates all S3 documents attached during this thread's lifetime.
	// Since this hook is instantiated per-thread by useRemoteThreadListRuntime,
	// the ref naturally resets when switching threads.
	const threadDocsRef = useRef<Map<number, S3DocRef>>(new Map());

	const adapter: ChatModelAdapter = useMemo(
		() => ({
			async *run({ messages, abortSignal }) {
				// Ensure thread is initialized and remoteId is available.
				// initialize() is idempotent — returns the existing promise if already called.
				// @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node"
				const { remoteId } = await aui.threadListItem().initialize();

				// Snapshot S3 document selections and clear the context immediately
				// so chips disappear from the composer as soon as the user sends.
				const s3Docs = s3DocsRef.current;
				clearS3Docs();

				// Accumulate newly attached docs into the thread-level map so they
				// are re-sent on every subsequent turn. The server uploads each doc
				// to the Anthropic Files API (with caching) so the AI always has
				// access to the full document — not just prior response summaries.
				for (const doc of s3Docs) {
					threadDocsRef.current.set(doc.documentId, {
						bucket: doc.bucket,
						documentId: doc.documentId,
						fileType: doc.fileType,
						key: doc.key,
						name: doc.name,
					});
				}

				// Convert ThreadMessage[] to UI message format for the API.
				// Attachments live in a separate `attachments` array on the ThreadMessage,
				// NOT merged into `content`. We flatten attachment content parts into `parts`.
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const uiMessages = messages.map((m: any, idx: number) => {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					const parts = m.content.map((part: any) => {
						if (part.type === 'text') return { type: 'text', text: part.text };
						if (part.type === 'tool-call')
							return {
								type: 'tool-invocation',
								state: 'result',
								toolCallId: part.toolCallId,
								toolName: part.toolName,
								args: part.args,
								result: part.result,
							};
						return part;
					});

					// Flatten attachment content into UIMessage file parts.
					// Each completed attachment has a `content` array, e.g.:
					//   { type: 'image', image: 'data:...' } or { type: 'file', data: base64, mimeType: '...' }
					for (const attachment of m.attachments ?? []) {
						for (const part of attachment.content ?? []) {
							if (part.type === 'image') {
								// UIMessage file parts use `url` for data URLs, NOT `data`.
								// Extract actual MIME type from the data URL (e.g. image/png, image/jpeg)
								const mediaType = (part.image as string).match(/^data:([^;]+)/)?.[1] ?? 'image/png';
								parts.push({ type: 'file', url: part.image, mediaType });
							} else if (part.type === 'file') {
								// For PDFs/other files: wrap raw base64 in a data URL
								const url = `data:${part.mimeType};base64,${part.data}`;
								parts.push({ type: 'file', url, mediaType: part.mimeType });
							} else if (part.type === 'text') {
								parts.push({ type: 'text', text: part.text });
							}
						}
					}

					// Inject S3 document metadata markers on the last user message.
					// These markers persist through Mastra's storage and enable
					// document card rendering on history reload.
					const isLastMessage = idx === messages.length - 1;
					if (isLastMessage && m.role === 'user' && s3Docs.length > 0) {
						for (const doc of s3Docs) {
							parts.push({
								type: 'text',
								text: buildS3DocMetaMarker({ documentId: doc.documentId, fileType: doc.fileType, name: doc.name }),
							});
						}
					}

					return { id: m.id, parts, role: m.role };
				});

				// Build S3 document refs for server-side presigned URL generation.
				// Uses the thread-level accumulator so ALL previously attached
				// documents are re-sent every turn (not just newly selected ones).
				const s3Documents: S3DocRef[] = Array.from(threadDocsRef.current.values());

				const response = await fetch('/api/chat/route', {
					body: JSON.stringify({
						id: remoteId,
						messages: uiMessages,
						pageUrl: window.location.pathname,
						...(s3Documents.length > 0 ? { s3Documents } : {}),
					}),
					headers: { 'Content-Type': 'application/json' },
					method: 'POST',
					signal: abortSignal,
				});

				if (!response.ok) throw new Error(`Chat API error: ${response.status}`);
				if (!response.body) throw new Error('No response body');

				yield* readAssistantReply(response.body);
			},
		}),
		[aui]
	);

	return useLocalRuntime(adapter, {
		adapters: {
			attachments: new SizeLimitedAdapter(
				new CompositeAttachmentAdapter([new SimpleImageAttachmentAdapter(), new SimpleTextAttachmentAdapter(), new PDFAttachmentAdapter()]),
				MAX_FILE_SIZE
			),
			dictation: dictationAdapter,
		},
	});
};

// Provides thread-specific history adapter via the unstable_Provider pattern.
// Runs in the context of each thread, so aui.threadListItem() returns the
// current thread's metadata (including remoteId).
const ThreadHistoryProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const aui = useAui();

	const history: ThreadHistoryAdapter = useMemo(
		() => ({
			async load() {
				// @ts-ignore — ScopeRegistry augmentation not resolved
				const remoteId = aui.threadListItem()?.getState().remoteId;
				if (!remoteId) return { headId: null, messages: [] };

				const res = await fetch(`/api/chat/threads/${encodeURIComponent(remoteId)}?messages=true`);
				if (!res.ok) return { headId: null, messages: [] };
				const data = await res.json();
				if (!data?.messages?.length) return { headId: null, messages: [] };

				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const messageLikes = data.messages.map((m: any) => {
					// If the server returned s3Documents metadata for this message,
					// reconstruct the markers in content so UserMessageS3Documents
					// can render document cards on history reload.
					const s3Docs: S3DocMeta[] | undefined = m.s3Documents;
					if (s3Docs?.length) {
						const textContent = m.content || '';
						const markers = s3Docs.map((d: S3DocMeta) => buildS3DocMetaMarker(d)).join(' ');
						return {
							content: textContent ? `${textContent} ${markers}` : markers,
							createdAt: m.createdAt ? new Date(m.createdAt) : undefined,
							id: m.id,
							role: m.role as 'user' | 'assistant',
						};
					}
					return {
						content: m.content,
						createdAt: m.createdAt ? new Date(m.createdAt) : undefined,
						id: m.id,
						role: m.role as 'user' | 'assistant',
					};
				});

				return ExportedMessageRepository.fromArray(messageLikes);
			},
			// No-op: Mastra handles persistence via agent.stream({ memory: ... })
			async append() {},
		}),
		[aui]
	);

	const adapters = useMemo(() => ({ history }), [history]);

	return <RuntimeAdapterProvider adapters={adapters}>{children}</RuntimeAdapterProvider>;
};

/**
 * A single adapter that delegates to mastraThreadListAdapter when the feature
 * is enabled and returns safe no-op responses when disabled. Using a single
 * stable adapter avoids swapping the adapter reference (which causes the
 * runtime to re-initialize and can leave the composer in a broken state).
 */
function useGatedThreadListAdapter(): RemoteThreadListAdapter {
	const router = useRouter();
	const { enabled, hasSession, sessionResolved } = useAssistantEnabled();
	const enabledRef = useRef(false);
	enabledRef.current = enabled;

	// `/api/chat/threads` authenticates with `getAuth`, so calling it before Clerk has a session 401s.
	// This provider sits under `ClerkProvider` in `_app`, above the layouts, so it can mount while the
	// session is still resolving — `list()` is deliberately ungated (see below) and would otherwise
	// fire into that window and throw. Tracked in a ref so the memoized adapter identity stays stable.
	const hasSessionRef = useRef(false);
	hasSessionRef.current = hasSession;

	// assistant-ui caches the first `list()` result permanently, so answering "no threads" while Clerk
	// is still rehydrating leaves the sidebar empty for the whole page life — the bug this resolves.
	// `list()` instead awaits this promise, which settles the moment Clerk reports `isLoaded`, so the
	// cached answer is always the real one. Signed-out visitors still resolve (to `hasSession: false`)
	// and get an empty list without ever calling the API.
	const sessionResolvedRef = useRef<{ promise: Promise<void>; resolve: () => void } | undefined>(undefined);
	if (!sessionResolvedRef.current) {
		let resolve!: () => void;
		const promise = new Promise<void>((r) => {
			resolve = r;
		});
		sessionResolvedRef.current = { promise, resolve };
	}
	if (sessionResolved) {
		sessionResolvedRef.current.resolve();
	}

	return useMemo<RemoteThreadListAdapter>(
		() => ({
			async archive(remoteId) {
				if (!enabledRef.current) return;
				return mastraThreadListAdapter.archive(remoteId);
			},
			async delete(remoteId) {
				if (!enabledRef.current) return;
				return mastraThreadListAdapter.delete(remoteId);
			},
			async fetch(threadId) {
				if (!enabledRef.current) return { remoteId: '', status: 'regular' as const };
				return mastraThreadListAdapter.fetch(threadId);
			},
			async generateTitle(remoteId, messages) {
				if (!enabledRef.current) {
					return createAssistantStream((controller) => {
						controller.appendText('New Chat');
						controller.close();
					});
				}
				return mastraThreadListAdapter.generateTitle(remoteId, messages);
			},
			async initialize(threadId) {
				if (!enabledRef.current) return { externalId: undefined, remoteId: threadId };
				return mastraThreadListAdapter.initialize(threadId);
			},
			async list() {
				// Always load threads regardless of feature-flag timing.
				// assistant-ui caches the result permanently on first call,
				// so gating this would leave the thread list empty forever.
				// Exception: Don't load threads on mobile or customer pages (both under this route)
				if (router.pathname.includes('customer-pages')) {
					return { threads: [] };
				}
				// Wait for Clerk rather than reporting "no threads" while it rehydrates: on a refresh
				// this call races the session, and because the result is cached permanently, answering
				// early would empty the sidebar until the next full page load.
				await sessionResolvedRef.current!.promise;
				// Resolved and genuinely signed out. Calling the API would 401, and the rejection
				// surfaces as an unhandled error; an anonymous visitor has no threads to show anyway.
				if (!hasSessionRef.current) {
					return { threads: [] };
				}
				return mastraThreadListAdapter.list();
			},
			async rename(remoteId, newTitle) {
				if (!enabledRef.current) return;
				return mastraThreadListAdapter.rename(remoteId, newTitle);
			},
			async unarchive(remoteId) {
				if (!enabledRef.current) return;
				return mastraThreadListAdapter.unarchive(remoteId);
			},
			unstable_Provider: ThreadHistoryProvider,
		}),
		[]
	);
}

const GlazierAssistantInner: FC<{ children: ReactNode }> = ({ children }) => {
	const adapter = useGatedThreadListAdapter();
	const { clearDocumentsRef, documentsRef } = useS3Docs();

	const aui = useAui({
		// @ts-ignore — ScopeRegistry augmentation from @assistant-ui/core not resolved with moduleResolution "node"
		suggestions: Suggestions(suggestions),
	});

	const runtimeHook = useMemo(() => () => useChatRuntimeHook(documentsRef, () => clearDocumentsRef.current()), [documentsRef, clearDocumentsRef]);

	const runtime = useRemoteThreadListRuntime({
		adapter,
		runtimeHook,
	});

	return (
		<AssistantRuntimeProvider aui={aui} runtime={runtime}>
			<TooltipProvider>{children}</TooltipProvider>
		</AssistantRuntimeProvider>
	);
};

export const GlazierAssistantProvider: FC<{ children: ReactNode }> = ({ children }) => {
	return (
		<S3DocProvider>
			<GlazierAssistantInner>{children}</GlazierAssistantInner>
		</S3DocProvider>
	);
};
